import React from "react";
import { Button, Alert, Box, Link, Typography } from "@mui/material";
import OTPInput from "../../../components/auth/OTPInput";

interface OTPFormProps {
  email: string;
  otpCode: string;
  onOtpChange: (code: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBackToEmail: () => void;
  onResendOTP: () => void;
  error: string | null;
  isLoading: boolean;
  isOtpValid: boolean;
  resendCooldown: number;
}

const darkButtonSx = {
  mt: 3,
  borderRadius: "16px",
  py: 1.5,
  background: "#8a1125",
  textTransform: "none",
  fontSize: "15px",
  fontWeight: 600,
  "&:hover": { background: "#c41830" },
  "&.Mui-disabled": {
    background: "rgba(138,17,37,0.3)",
    color: "rgba(255,255,255,0.3)",
  },
};

const darkInfoAlertSx = {
  mb: 2,
  background: "rgba(2,136,209,0.1)",
  color: "rgba(255,255,255,0.8)",
  border: "1px solid rgba(2,136,209,0.15)",
  borderRadius: "12px",
  "& .MuiAlert-icon": { color: "rgba(2,136,209,0.8)" },
};

const darkErrorAlertSx = {
  mt: 2,
  background: "rgba(244,44,4,0.1)",
  color: "#ff6b4a",
  border: "1px solid rgba(244,44,4,0.2)",
  borderRadius: "12px",
  "& .MuiAlert-icon": { color: "#ff6b4a" },
};

const darkLinkSx = {
  color: "rgba(255,255,255,0.6)",
  cursor: "pointer",
  textDecoration: "none",
  "&:hover": { color: "white" },
  transition: "color 0.3s",
};

export const OTPForm: React.FC<OTPFormProps> = ({
  email,
  otpCode,
  onOtpChange,
  onSubmit,
  onBackToEmail,
  onResendOTP,
  error,
  isLoading,
  isOtpValid,
  resendCooldown,
}) => {
  return (
    <form onSubmit={onSubmit}>
      <Alert severity="info" sx={darkInfoAlertSx}>
        En verifieringskod har skickats till {email}
      </Alert>

      <Typography
        variant="body2"
        align="center"
        sx={{ mb: 1, color: "rgba(255,255,255,0.7) !important" }}
      >
        Ange verifieringskod
      </Typography>

      <OTPInput
        value={otpCode}
        onChange={onOtpChange}
        disabled={isLoading}
        error={!!error}
      />

      {error && (
        <Alert severity="error" sx={darkErrorAlertSx}>
          {error}
        </Alert>
      )}

      <Button
        fullWidth
        type="submit"
        variant="contained"
        size="large"
        disabled={isLoading || !isOtpValid}
        sx={darkButtonSx}
      >
        {isLoading ? "Verifierar..." : "Verifiera"}
      </Button>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={onBackToEmail}
          disabled={isLoading}
          sx={darkLinkSx}
        >
          Ändra e-post
        </Link>

        {resendCooldown > 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.4) !important" }}
          >
            Skicka igen om {resendCooldown}s
          </Typography>
        ) : (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={onResendOTP}
            disabled={isLoading}
            sx={darkLinkSx}
          >
            Skicka kod igen
          </Link>
        )}
      </Box>
    </form>
  );
};
